import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Grid,
  Divider,
} from '@mui/material';
import React, {useContext, useEffect, useState} from 'react';
import APIWrapperContext from '../../../../services/APIWrapperContext';
import {ArbeitszeitService, UserService} from '../../../../services/openapi';
import {visuallyHidden} from '@mui/utils';

// Interface für die Mitarbeiterdaten
interface IMitarbeiterUrlaub {
  user_id: string;
  fullname: string;
  email: string;
  total_vacation_days: number;
  initial_remaining_days: number;
  monthly_vacation_days: boolean;
}

// Sortier-Typen
type Order = 'asc' | 'desc';

// Interface für die Tabellen-Spalten
interface HeadCell {
  disablePadding: boolean;
  id: keyof IMitarbeiterUrlaub;
  label: string;
  numeric: boolean;
}

// Definition der Tabellen-Spalten
const headCells: readonly HeadCell[] = [
  {
    id: 'fullname',
    numeric: false,
    disablePadding: false,
    label: 'Mitarbeiter/-in',
  },
  {
    id: 'total_vacation_days',
    numeric: true,
    disablePadding: false,
    label: 'Gesamturlaub (Tage)',
  },
  {
    id: 'initial_remaining_days',
    numeric: true,
    disablePadding: false,
    label: 'Resturlaub (Tage)',
  },
  {
    id: 'monthly_vacation_days',
    numeric: false,
    disablePadding: false,
    label: 'Monatlicher Urlaub',
  },
];

// Vergleichsfunktion für das Sortieren
function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

// Komparator-Funktion
function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: {[key in Key]: number | string | boolean},
  b: {[key in Key]: number | string | boolean}
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// Stabile Sortierung
function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

// Props für den Tabellen-Header
interface EnhancedTableProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof IMitarbeiterUrlaub) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

// Tabellen-Header-Komponente
function EnhancedTableHead(props: EnhancedTableProps) {
  const {order, orderBy, onRequestSort} = props;
  const createSortHandler =
    (property: keyof IMitarbeiterUrlaub) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}>
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}>
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component='span' sx={visuallyHidden as any}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export default function Urlaubsuebersicht() {
  const {call_backend_api} = useContext(APIWrapperContext);
  const [mitarbeiter, setMitarbeiter] = useState<IMitarbeiterUrlaub[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof IMitarbeiterUrlaub>('fullname');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Daten vom Backend laden
  const loadMitarbeiterData = () => {
    setLoading(true);
    Promise.all([
      new Promise<any[]>((resolve) => {
        call_backend_api(
          ArbeitszeitService.getBearbeiterForUsersArbeitzeitDashboardWorkerGetBearbeiterGet(),
          (res: any) => resolve(res),
          () => resolve([]),
          undefined,
          false
        );
      }),
      new Promise<any[]>((resolve) => {
        call_backend_api(
          UserService.getAllVacationDaysUserGetAllVacationDaysGet(),
          (res: any) => resolve(res),
          () => resolve([]),
          undefined,
          false
        );
      }),
    ]).then(([users, vacationDays]) => {
      const vacationMap = new Map<string, any>(
        (vacationDays as any[]).map((v: any) => [v.user_id, v])
      );

      const mergedUsers: IMitarbeiterUrlaub[] = (users as any[])
        .map((user: any) => {
          const vacationData = vacationMap.get(user.user_id) || {};
          return {
            user_id: user.user_id,
            fullname: user.fullname || user.email,
            email: user.email,
            total_vacation_days: vacationData.total_vacation_days || 0,
            initial_remaining_days: vacationData.initial_remaining_days || 0,
            monthly_vacation_days: vacationData.monthly_vacation_days || false,
          };
        })
        .sort((a, b) => {
          // Sortiere nach Nachnamen
          const getLastName = (fullname: string) => {
            const nameParts = fullname.split(' ');
            return nameParts[nameParts.length - 1];
          };

          const lastNameA = getLastName(a.fullname);
          const lastNameB = getLastName(b.fullname);

          return lastNameA.localeCompare(lastNameB, 'de');
        });

      setMitarbeiter(mergedUsers);
      setLoading(false);
    });
  };

  useEffect(() => {
    loadMitarbeiterData();
  }, []);

  // Sortier-Handler
  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof IMitarbeiterUrlaub
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Berechne sichtbare Zeilen
  const visibleRows = React.useMemo(
    () => stableSort(mitarbeiter, getComparator(order, orderBy)),
    [order, orderBy, mitarbeiter]
  );

  if (loading) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', p: 3}}>
        <CircularProgress />
      </Box>
    );
  }

  if (mitarbeiter.length === 0) {
    return (
      <Box sx={{p: 2}}>
        <Typography variant='body1'>Keine Mitarbeiterdaten verfügbar</Typography>
      </Box>
    );
  }

  // Mobile layout with cards
  if (isMobile) {
    return (
      <Box sx={{p: 1}}>
        {visibleRows.map((row, index) => (
          <Paper
            key={row.user_id}
            elevation={2}
            sx={{
              mb: 2,
              overflow: 'hidden',
              borderRadius: 1,
            }}>
            <Box
              sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                bgcolor: 'rgba(0, 0, 0, 0.03)',
              }}>
              <Typography variant='subtitle1' fontWeight='medium'>
                {row.fullname}
              </Typography>
            </Box>
            <Divider />
            <Box sx={{p: 2}}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant='body2' color='text.secondary'>
                    Gesamturlaub:
                  </Typography>
                  <Typography variant='body1' fontWeight='medium'>
                    {row.total_vacation_days} Tage
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant='body2' color='text.secondary'>
                    Resturlaub:
                  </Typography>
                  <Typography variant='body1' fontWeight='medium'>
                    {row.initial_remaining_days} Tage
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant='body2' color='text.secondary'>
                    Monatlicher Urlaub:
                  </Typography>
                  <Typography variant='body1'>
                    {row.monthly_vacation_days ? 'Ja' : 'Nein'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        ))}
      </Box>
    );
  }

  // Desktop layout with table
  return (
    <TableContainer sx={{overflowX: 'auto'}}>
      <Table>
        <TableHead>
          <TableRow>
            {headCells.map((headCell) => (
              <TableCell
                key={headCell.id}
                align={headCell.numeric ? 'right' : 'left'}
                padding={headCell.disablePadding ? 'none' : 'normal'}
                sortDirection={orderBy === headCell.id ? order : false}
                sx={{fontWeight: 'bold'}}>
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : 'asc'}
                  onClick={() => handleRequestSort({} as React.MouseEvent<unknown>, headCell.id)}>
                  {headCell.label}
                  {orderBy === headCell.id ? (
                    <Box component='span' sx={visuallyHidden as any}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleRows.map((row, index) => (
            <TableRow key={row.user_id} hover>
              <TableCell sx={{fontWeight: 'medium'}}>{row.fullname}</TableCell>
              <TableCell align='right'>{row.total_vacation_days}</TableCell>
              <TableCell align='right'>{row.initial_remaining_days}</TableCell>
              <TableCell align='left'>{row.monthly_vacation_days ? 'Ja' : 'Nein'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
